import React, { useRef, useEffect } from "react";
import { sumRows } from "../tableTotalUtils";

import {
  TH_BG_BAND,
} from "../../reportTableConfig";

const REPORT_FONT = '"Roboto", "Helvetica", "Arial", sans-serif';

/** Sign after rounding to display precision (-1, 0, 1). */
export function displaySign(v, decimals = 0) {
  if (v == null || v === "") return 0;
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  const factor = 10 ** decimals;
  const rounded = Math.round(n * factor) / factor;
  if (rounded === 0) return 0;
  return rounded < 0 ? -1 : 1;
}

export function isDisplayNegative(v, decimals = 0) {
  return displaySign(v, decimals) < 0;
}

function roundedValue(v, decimals = 0) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
}

/** Percentage cells — negatives in parentheses (Scenario MoM standard). */
export function fmtPctScenario(v) {
  if (v == null || v === "") return "";
  const n = roundedValue(v, 2);
  if (n == null) return "";
  if (n === 0) return "0.00%";
  if (n < 0) return `(${Math.abs(n).toFixed(2)}%)`;
  return `${n.toFixed(2)}%`;
}

const SC_MONTHS = [
  { key: "jan" }, { key: "feb" }, { key: "mar" }, { key: "apr" },
  { key: "may" }, { key: "jun" }, { key: "jul" }, { key: "aug" },
  { key: "sep" }, { key: "oct" }, { key: "nov" }, { key: "dec" },
];

export const METRIC_BANDS = [
  {
    key: "rev",  label: "Revenue",
    bg: TH_BG_BAND,
    get: (row, m) => row[`rev_${m}`],
    total: (row) => SC_MONTHS.reduce((s, m) => s + (row[`rev_${m.key}`] ?? 0), 0),
  },
  {
    key: "dl",   label: "Direct Labor",
    bg: "#162032",
    get: (row, m) => row[`dl_${m}`],
    total: (row) => SC_MONTHS.reduce((s, m) => s + (row[`dl_${m.key}`] ?? 0), 0),
  },
  {
    key: "subk", label: "Subcontractor",
    bg: "#1B2A42",
    get: (row, m) => {
      if (`subk_${m}` in row) return row[`subk_${m}`];
      const d = row[`dl_${m}`];
      return d != null ? Math.round(d * 0.28) : null;
    },
    total: (row) => SC_MONTHS.reduce((s, m) => {
      if (`subk_${m.key}` in row) return s + (row[`subk_${m.key}`] ?? 0);
      const d = row[`dl_${m.key}`];
      return s + (d != null ? Math.round(d * 0.28) : 0);
    }, 0),
  },
  {
    key: "odc",  label: "ODCs",
    bg: "#1A1F3A",
    get: (row, m) => {
      if (`odc_${m}` in row) return row[`odc_${m}`];
      const d = row[`dl_${m}`];
      return d != null ? Math.round(d * 0.09) : null;
    },
    total: (row) => SC_MONTHS.reduce((s, m) => {
      if (`odc_${m.key}` in row) return s + (row[`odc_${m.key}`] ?? 0);
      const d = row[`dl_${m.key}`];
      return s + (d != null ? Math.round(d * 0.09) : 0);
    }, 0),
  },
  {
    key: "tdc",  label: "Total Direct Cost",
    bg: "#162032",
    get: (row, m) => {
      if (`tdc_${m}` in row) return row[`tdc_${m}`];
      const d = row[`dl_${m}`];
      return d != null ? d + Math.round(d * 0.28) + Math.round(d * 0.09) : null;
    },
    total: (row) => SC_MONTHS.reduce((s, m) => {
      if (`tdc_${m.key}` in row) return s + (row[`tdc_${m.key}`] ?? 0);
      const d = row[`dl_${m.key}`];
      return s + (d != null ? d + Math.round(d * 0.28) + Math.round(d * 0.09) : 0);
    }, 0),
  },
  {
    key: "gp",   label: "Gross Margin",
    bg: "#0D3040",
    get: (row, m) => {
      if (`gp_${m}` in row) return row[`gp_${m}`];
      const r = row[`rev_${m}`], d = row[`dl_${m}`];
      if (r == null || d == null) return null;
      return r - d - Math.round(d * 0.28) - Math.round(d * 0.09);
    },
    total: (row) => SC_MONTHS.reduce((s, m) => {
      if (`gp_${m.key}` in row) return s + (row[`gp_${m.key}`] ?? 0);
      const r = row[`rev_${m.key}`], d = row[`dl_${m.key}`];
      return s + (r != null && d != null ? r - d - Math.round(d * 0.28) - Math.round(d * 0.09) : 0);
    }, 0),
  },
  {
    key: "gmpct", label: "Gross Margin %",
    bg: "#0D3040",
    fmt: fmtPctScenario,
    get: (row, m) => {
      // API sends this as a ratio (0.238, not 23.8) — multiply by 100 to match the
      // client-computed fallback below and what fmtPctScenario expects.
      if (`gmpct_${m}` in row) {
        const v = row[`gmpct_${m}`];
        return v == null ? null : v * 100;
      }
      const r = row[`rev_${m}`], d = row[`dl_${m}`];
      if (r == null || d == null) return null;
      if (!r) return 0;
      return ((r - d - Math.round(d * 0.28) - Math.round(d * 0.09)) / r) * 100;
    },
    total: (row) => {
      if (`gmpct_total` in row) {
        const v = row[`gmpct_total`];
        return v == null ? null : v * 100;
      }
      const totalRev = SC_MONTHS.reduce((s, m) => s + (row[`rev_${m.key}`] ?? 0), 0);
      if (totalRev === 0) return 0;
      const totalGP = SC_MONTHS.reduce((s, m) => {
        const r = row[`rev_${m.key}`], d = row[`dl_${m.key}`];
        return s + (r != null && d != null ? r - d - Math.round(d * 0.28) - Math.round(d * 0.09) : 0);
      }, 0);
      return (totalGP / totalRev) * 100;
    },
  },
  {
    key: "indirect", label: "Indirect Cost",
    bg: "#1A1F3A",
    get: (row, m) => row[`indirect_${m}`] ?? null,
    total: (row) => SC_MONTHS.reduce((s, m) => s + (row[`indirect_${m.key}`] ?? 0), 0),
  },
  {
    key: "burden", label: "Burdened Cost",
    bg: "#162032",
    get: (row, m) => {
      if (`burden_${m}` in row) return row[`burden_${m}`];
      const d = row[`dl_${m}`], ind = row[`indirect_${m}`];
      if (d == null || ind == null) return null;
      return d + Math.round(d * 0.28) + Math.round(d * 0.09) + ind;
    },
    total: (row) => {
      if (`burden_total` in row) return row[`burden_total`] ?? null;
      let hasIndirect = false;
      const sum = SC_MONTHS.reduce((s, m) => {
        const d = row[`dl_${m.key}`], ind = row[`indirect_${m.key}`];
        if (d == null || ind == null) return s;
        hasIndirect = true;
        return s + d + Math.round(d * 0.28) + Math.round(d * 0.09) + ind;
      }, 0);
      return hasIndirect ? sum : null;
    },
  },
  {
    key: "np", label: "Net Profit",
    bg: "#0D3040",
    get: (row, m) => {
      if (`np_${m}` in row) return row[`np_${m}`];
      const r = row[`rev_${m}`], d = row[`dl_${m}`], ind = row[`indirect_${m}`];
      if (r == null || d == null || ind == null) return null;
      return r - (d + Math.round(d * 0.28) + Math.round(d * 0.09) + ind);
    },
    total: (row) => {
      if (`np_total` in row) return row[`np_total`] ?? null;
      let hasAll = false;
      const sum = SC_MONTHS.reduce((s, m) => {
        const r = row[`rev_${m.key}`], d = row[`dl_${m.key}`], ind = row[`indirect_${m.key}`];
        if (r == null || d == null || ind == null) return s;
        hasAll = true;
        return s + r - (d + Math.round(d * 0.28) + Math.round(d * 0.09) + ind);
      }, 0);
      return hasAll ? sum : null;
    },
  },
  {
    key: "nmpct", label: "Net Margin %",
    bg: "#0D3040",
    fmt: fmtPctScenario,
    get: (row, m) => {
      // API sends this as a ratio (0.091, not 9.1) — multiply by 100, same as gmpct above.
      if (`nmpct_${m}` in row) {
        const v = row[`nmpct_${m}`];
        return v == null ? null : v * 100;
      }
      const r = row[`rev_${m}`], d = row[`dl_${m}`], ind = row[`indirect_${m}`];
      if (r == null || d == null || ind == null) return null;
      if (!r) return 0;
      return ((r - (d + Math.round(d * 0.28) + Math.round(d * 0.09) + ind)) / r) * 100;
    },
    total: (row) => {
      if (`nmpct_total` in row) {
        const v = row[`nmpct_total`];
        return v == null ? null : v * 100;
      }
      const totalRev = SC_MONTHS.reduce((s, m) => s + (row[`rev_${m.key}`] ?? 0), 0);
      if (totalRev === 0) return 0;
      let hasIndirect = false;
      const totalNP = SC_MONTHS.reduce((s, m) => {
        const r = row[`rev_${m.key}`], d = row[`dl_${m.key}`], ind = row[`indirect_${m.key}`];
        if (r == null || d == null || ind == null) return s;
        hasIndirect = true;
        return s + r - (d + Math.round(d * 0.28) + Math.round(d * 0.09) + ind);
      }, 0);
      return hasIndirect ? (totalNP / totalRev) * 100 : null;
    },
  },
];

/** Default visible metric bands — revenue only; direct labor hidden until user enables it. */
export const DEFAULT_VISIBLE_BANDS = ["rev"];

export function bandTotalForMonths(band, row, months) {
  const active = months?.length ? months : SC_MONTHS;
  if (band.key === "gmpct") {
    const totalRev = active.reduce((s, m) => s + (row[`rev_${m.key}`] ?? 0), 0);
    if (totalRev === 0) return 0;
    const totalGP = active.reduce((s, m) => {
      const r = row[`rev_${m.key}`], d = row[`dl_${m.key}`];
      return s + (r != null && d != null ? r - d - Math.round(d * 0.28) - Math.round(d * 0.09) : 0);
    }, 0);
    return (totalGP / totalRev) * 100;
  }
  if (band.key === "nmpct") {
    const totalRev = active.reduce((s, m) => s + (row[`rev_${m.key}`] ?? 0), 0);
    if (totalRev === 0) return 0;
    let hasIndirect = false;
    const totalNP = active.reduce((s, m) => {
      const r = row[`rev_${m.key}`], d = row[`dl_${m.key}`], ind = row[`indirect_${m.key}`];
      if (r == null || d == null || ind == null) return s;
      hasIndirect = true;
      return s + r - (d + Math.round(d * 0.28) + Math.round(d * 0.09) + ind);
    }, 0);
    return hasIndirect ? (totalNP / totalRev) * 100 : null;
  }
  return active.reduce((s, m) => s + (Number(band.get(row, m.key)) || 0), 0);
}

/** Build a synthetic total row by summing filtered contract rows. */
export function aggregateScenarioMomRow(filteredRows, activeBands, activeMonths) {
  if (!filteredRows?.length) return null;
  const out = {};
  for (const band of activeBands) {
    for (const m of activeMonths) {
      const field = `${band.key}_${m.key}`;
      out[field] = sumRows(filteredRows, (r) => band.get(r, m.key));
    }
  }
  return out;
}

export { SC_MONTHS };

export function ScenarioColumnPicker({ visible, onToggle }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = useRef(null);

  const hiddenCount = METRIC_BANDS.length - visible.length;
  const isActive    = open || hiddenCount > 0;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <button
        type="button"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, height: 34,
          padding: "0 12px", borderRadius: 6, fontFamily: REPORT_FONT,
          border: `1px solid ${isActive ? "#C7D2FE" : "#E2E8F0"}`,
          background: isActive ? "#EEF2FF" : "#fff",
          color: isActive ? "#4F46E5" : "#374151",
          fontSize: 12, fontWeight: 500, cursor: "pointer",
        }}
        onClick={() => setOpen((p) => !p)}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/>
        </svg>
        Columns
        {hiddenCount > 0 && (
          <span style={{
            display: "inline-flex", alignItems: "center", height: 18, padding: "0 7px",
            borderRadius: 10, background: "#4F46E5", color: "#fff", fontSize: 11, fontWeight: 600,
          }}>{hiddenCount} Hidden</span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 1200,
          background: "#fff", borderRadius: 10, border: "1px solid #E5E7EB",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 240,
          maxHeight: 420, overflowY: "auto",
          fontFamily: REPORT_FONT,
        }}>
          <div style={{
            padding: "8px 14px", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0",
            fontSize: 12, fontWeight: 700, color: "#111827",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span>Visible Metrics</span>
            <span style={{ fontSize: 11, color: "#6B7280" }}>{visible.length} of {METRIC_BANDS.length}</span>
          </div>
          <div style={{ overflowY: "auto", padding: "4px 0 8px" }}>
            {METRIC_BANDS.map((band) => {
              const checked = visible.includes(band.key);
              return (
                <label key={band.key} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "7px 14px", cursor: "pointer", fontSize: 12, color: "#374151",
                }}>
                  <input type="checkbox" checked={checked} onChange={() => onToggle(band.key)}
                    style={{ width: 14, height: 14, accentColor: "#4F46E5", cursor: "pointer", flexShrink: 0 }} />
                  <span>{band.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
